/* ---------------------------------------------------------------------------
   The Judge's Stash — buy box.

   Owns the format / plan / quantity selection, the prices it shows, and the
   add-to-cart. Prices are derived from the live product prices rendered into
   the markup by Liquid, using the same integer arithmetic Liquid uses, so the
   page and the server never disagree by a rounding cent.

   IMPORTANT: the quantity discounts shown for one-time purchases are display
   only. Shopify applies them at the cart via an automatic discount — if that
   discount is missing or expires, the page will promise a price the cart does
   not honour. See INSTALL-INSTRUCTIONS.txt.

   Cart UI: this storefront shows SlideCart HQ (window.SLIDECART_*). Adding via
   /cart/add.js alone leaves SlideCart displaying a stale, usually empty cart —
   it has to be handed the fresh cart with SLIDECART_SET_CART(cart) and then
   opened. See addToCart below.

   Click hijacking: a cart app here registers a document-level CAPTURE click
   listener that matches links pointing at /cart, calls preventDefault and
   stopPropagation, and opens its own drawer. Any handler bound to the button
   loses that race, so the add never fires and the shopper sees an empty drawer.
   Two things keep this working: the button's href is javascript:void(0) (nothing
   for the app to match), and the handler below binds in the capture phase.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];

    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return whole + '.' + parts[1];
        case 'amount_no_decimals': return whole;
        case 'amount_with_comma_separator': return withComma;
        case 'amount_no_decimals_with_comma_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        case 'amount_with_apostrophe_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'") + '.' + parts[1];
        default: return whole + '.' + parts[1];
      }
    });
  }

  function BuyBox(root) {
    this.root = root;
    this.config = {};
    var configEl = root.querySelector('[data-jsn-buybox-config]');
    if (configEl) {
      try { this.config = JSON.parse(configEl.textContent); } catch (e) {}
    }
    this.formats = Array.prototype.slice.call(root.querySelectorAll('[data-jsn-format]'));
    this.plans = Array.prototype.slice.call(root.querySelectorAll('[data-jsn-plan]'));
    this.tiers = Array.prototype.slice.call(root.querySelectorAll('[data-jsn-qty]'));
    this.button = root.querySelector('[data-jsn-add]');
    this.bind();
    this.update();
  }

  BuyBox.prototype.selectedFormat = function () {
    var checked = this.formats.filter(function (el) {
      var radio = el.querySelector('.jsn-choice-bx-rad');
      return radio && radio.checked;
    });
    return checked[0] || this.formats[0];
  };

  BuyBox.prototype.selectedPlan = function () {
    var active = this.plans.filter(function (el) { return el.classList.contains('jsn-active'); });
    return active[0] || this.plans[0];
  };

  BuyBox.prototype.selectedTier = function () {
    var active = this.tiers.filter(function (el) { return el.classList.contains('jsn-active'); });
    return active[0] || this.tiers[0];
  };

  BuyBox.prototype.bind = function () {
    var self = this;

    this.formats.forEach(function (el) {
      el.addEventListener('click', function () {
        var radio = el.querySelector('.jsn-choice-bx-rad');
        if (radio) radio.checked = true;
        self.update();
      });
    });

    this.plans.forEach(function (el) {
      el.addEventListener('click', function () {
        self.plans.forEach(function (other) { other.classList.remove('jsn-active'); });
        el.classList.add('jsn-active');
        var radio = el.querySelector('input[name="buyopt"]');
        if (radio) radio.checked = true;
        self.update();
      });
    });

    this.tiers.forEach(function (el) {
      el.addEventListener('click', function () {
        self.tiers.forEach(function (other) { other.classList.remove('jsn-active'); });
        el.classList.add('jsn-active');
        var radio = el.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        self.update();
      });
    });

    if (this.button) {
      // Capture phase + stopPropagation: cart apps on this storefront listen for
      // clicks on anything cart-shaped and cancel them. The button's href is
      // javascript:void(0) so there is nothing for them to match on, and this
      // claims the event before any delegated handler can swallow it.
      this.button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (self.busy) return;
        self.addToCart();
      }, true);
      this.button.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.addToCart(); }
      });
    }
  };

  BuyBox.prototype.priceFor = function (base, qty, tierDiscount, subscribing) {
    // mirrors Liquid's integer arithmetic exactly
    if (subscribing) {
      var unit = Math.floor((base * (100 - this.config.subscribeDiscount)) / 100);
      return unit * qty;
    }
    return Math.floor((base * qty * (100 - tierDiscount)) / 100);
  };

  BuyBox.prototype.update = function () {
    var format = this.selectedFormat();
    var plan = this.selectedPlan();
    if (!format || !plan) return;

    var base = parseInt(format.getAttribute('data-price'), 10) || 0;
    var subscribing = plan.getAttribute('data-jsn-plan') === 'subscribe';
    var money = this.config.moneyFormat;
    var self = this;

    this.tiers.forEach(function (tier) {
      var qty = parseInt(tier.getAttribute('data-jsn-qty'), 10) || 1;
      var discount = parseInt(tier.getAttribute('data-jsn-discount'), 10) || 0;
      var target = tier.querySelector('.jsn-quantity-selector_price');
      if (target) {
        target.textContent = formatMoney(self.priceFor(base, qty, discount, subscribing), money);
      }
    });

    // the kit note only belongs to the Spirit + Kit format
    var isKit = format.getAttribute('data-jsn-format') === this.config.waterpipeFormat;
    Array.prototype.forEach.call(this.root.querySelectorAll('.jsn-waterpipe_text'), function (el) {
      el.style.display = isKit ? '' : 'none';
    });

    if (this.config.planPriceMode !== 'fixed') {
      this.plans.forEach(function (el) {
        var target = el.querySelector('.jsn-reg-prc');
        if (!target) return;
        var isSub = el.getAttribute('data-jsn-plan') === 'subscribe';
        var value = isSub ? Math.floor((base * (100 - self.config.subscribeDiscount)) / 100) : base;
        target.textContent = formatMoney(value, money);
      });
    }
  };

  // The label swap replaces the button's existing text node in place, so no
  // element is added or removed and the button cannot reflow. The button is
  // fixed-height with line-height == height, so the text simply changes.
  BuyBox.prototype.setBusy = function (on) {
    var btn = this.button;
    if (!btn) return;

    if (on) {
      var busyLabel = btn.getAttribute('data-jsn-loading-label');
      if (busyLabel) {
        for (var i = 0; i < btn.childNodes.length; i++) {
          var node = btn.childNodes[i];
          if (node.nodeType === 3 && node.nodeValue.trim()) {
            this.labelNode = node;
            this.labelText = node.nodeValue;
            node.nodeValue = busyLabel;
            break;
          }
        }
      }
      btn.setAttribute('data-jsn-loading', '1');
      btn.setAttribute('aria-busy', 'true');
    } else {
      if (this.labelNode) this.labelNode.nodeValue = this.labelText;
      btn.removeAttribute('data-jsn-loading');
      btn.removeAttribute('aria-busy');
    }
  };

  BuyBox.prototype.addToCart = function () {
    var format = this.selectedFormat();
    var plan = this.selectedPlan();
    var tier = this.selectedTier();
    if (!format || !tier) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var variantId = format.getAttribute('data-variant');
    if (!variantId) {
      window.location.href = root + 'cart';
      return;
    }

    var item = {
      id: Number(variantId),
      quantity: parseInt(tier.getAttribute('data-jsn-qty'), 10) || 1
    };

    if (plan && plan.getAttribute('data-jsn-plan') === 'subscribe') {
      var sellingPlan = format.getAttribute('data-selling-plan');
      if (sellingPlan) item.selling_plan = Number(sellingPlan);
    }

    // one add at a time — the endpoint takes ~700ms and three impatient clicks
    // would otherwise put three times the quantity in the cart
    if (this.busy) return;
    this.busy = true;
    this.setBusy(true);
    var self2 = this;

    // This storefront's visible cart is SlideCart HQ. A Dawn-style <cart-drawer>
    // also exists in the DOM but is not what the shopper sees, so refreshing that
    // leaves SlideCart showing its stale (usually empty) state. Prefer SlideCart,
    // and keep the Dawn path as a fallback so the section stays portable.
    var slideCart = typeof window.SLIDECART_SET_CART === 'function';
    var drawer = slideCart ? null
               : (document.querySelector('cart-drawer') || document.querySelector('cart-notification'));

    var body = { items: [item] };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
    }

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) { return response.json(); })
      .then(function (state) {
        if (state.status) throw new Error(state.description || state.message);

        if (slideCart) {
          // add.js returns only the lines just added — SlideCart needs the whole cart
          return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              window.SLIDECART_SET_CART(cart);
              if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
            });
        }

        if (drawer && typeof drawer.renderContents === 'function') {
          if (drawer.classList) drawer.classList.remove('is-empty');
          drawer.renderContents(state);
          if (typeof drawer.open === 'function') drawer.open();
          return;
        }

        window.location.href = root + 'cart';
      })
      .catch(function () {
        window.location.href = root + 'cart';
      })
      .finally(function () {
        self2.busy = false;
        self2.setBusy(false);
      });
  };

  var instances = [];

  function init(scope) {
    var boxes = (scope || document).querySelectorAll('[data-jsn-buybox]');
    Array.prototype.forEach.call(boxes, function (el) {
      if (instances.indexOf(el) !== -1) return;
      instances.push(el);
      new BuyBox(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    instances = instances.filter(function (el) { return el.isConnected; });
    init(e.target);
  });
})();
